/**
 * imageCompositor.ts  — v3
 *
 * Infographic: 100% Canvas-rendered — rich gradient bg, giant hero stat,
 * 3-column stat cards, programmatic bar chart, bullet insights.
 *
 * Comic Strip: 6-panel layout (2×3 portrait) with a UNIQUE FLUX illustration
 * per panel, matching the reference design:
 *   - Purple/teal title banner at top
 *   - 6 panels in 2 columns × 3 rows, each with its own art
 *   - Panel number badge (top-left), emoji badge (top-right)
 *   - Caption strip (dark, italic, below badge row)
 *   - Speech bubble (white, rounded, tail pointer, bottom of panel)
 *   - Gradient moral-of-story footer
 */

export interface ComicPanel {
  panel: number;
  setting: string;
  characters: string;
  action: string;
  dialogue: string;
  caption: string;
  emoji: string;
}

export interface ComicStrip {
  title: string;
  concept: string;
  style: string;
  panels: ComicPanel[];
  moralOfStory: string;
}

export interface InfographicData {
  title: string;
  subtitle?: string;
  statHighlights?: { label: string; value: string }[];
  dataPoints?: string[];
  colorScheme?: string;
  suggestedLayout?: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Load an image element from a src string.
 *  - For base64/blob data URLs: load directly (no CORS issues).
 *  - For external URLs: set crossOrigin and retry up to `retries` times. */
function loadImage(src: string, retries = 3): Promise<HTMLImageElement> {
  const isDataUrl = src.startsWith('data:') || src.startsWith('blob:');
  const attempt = (triesLeft: number): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      if (!isDataUrl) img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => {
        if (!isDataUrl && triesLeft > 1) {
          setTimeout(() => attempt(triesLeft - 1).then(resolve, reject), 1200 * (4 - triesLeft));
        } else {
          reject(new Error(`Failed to load image: ${src.slice(0, 80)}…`));
        }
      };
      img.src = src;
    });
  return attempt(isDataUrl ? 1 : retries);
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const test = current ? `${current} ${word}` : word;
    if (ctx.measureText(test).width <= maxWidth) {
      current = test;
    } else {
      if (current) lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);
  return lines.length > 0 ? lines : [''];
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

// ─── Infographic (Canvas + optional Pollinations reference photo header) ────

/**
 * Renders a magazine-quality infographic on Canvas.
 * If baseImageSrc is a URL/base64, it is rendered as a cinematic header photo
 * (top 30% of canvas) with a gradient fade-out, giving a real visual reference.
 * Stats, charts, and insights are rendered below on a rich dark background.
 */
export async function compositeInfographic(
  baseImageSrc: string,   // optional reference photo URL or base64
  infographic: InfographicData,
  trendName: string
): Promise<string> {
  // Canvas size: 1080 × 1420 (portrait, slightly taller for photo header)
  const W = 1080;
  const H = 1420;
  const PHOTO_H = 320; // height of the reference photo header strip
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d')!;

  const PAD = 60; // horizontal padding

  // ── Full background gradient ─────────────────────────────────────────────
  const bgGrad = ctx.createLinearGradient(0, 0, W, H);
  bgGrad.addColorStop(0, '#0a0818');
  bgGrad.addColorStop(0.3, '#0f0c29');
  bgGrad.addColorStop(0.7, '#1a1040');
  bgGrad.addColorStop(1, '#0d1b2a');
  ctx.fillStyle = bgGrad;
  ctx.fillRect(0, 0, W, H);

  // ── Reference photo header (cinematic strip at top) ──────────────────────
  let photoLoaded = false;
  if (baseImageSrc) {
    try {
      const photoImg = await loadImage(baseImageSrc);
      // Cover-fit photo into top strip
      const imgAspect = photoImg.naturalWidth / photoImg.naturalHeight;
      const stripAspect = W / PHOTO_H;
      let sx = 0, sy = 0, sw = photoImg.naturalWidth, sh = photoImg.naturalHeight;
      if (imgAspect > stripAspect) {
        sw = photoImg.naturalHeight * stripAspect;
        sx = (photoImg.naturalWidth - sw) / 2;
      } else {
        sh = photoImg.naturalWidth / stripAspect;
        sy = (photoImg.naturalHeight - sh) / 3; // slightly top-aligned
      }
      ctx.save();
      ctx.globalAlpha = 0.72;
      ctx.drawImage(photoImg, sx, sy, sw, sh, 0, 0, W, PHOTO_H);
      ctx.globalAlpha = 1;
      ctx.restore();
      photoLoaded = true;
    } catch { /* photo unavailable — fallback to pure gradient */ }
  }

  // Gradient fade-out over photo → blends into dark background
  if (photoLoaded) {
    const fadeGrad = ctx.createLinearGradient(0, 0, 0, PHOTO_H);
    fadeGrad.addColorStop(0, 'rgba(10,8,24,0.15)');
    fadeGrad.addColorStop(0.55, 'rgba(10,8,24,0.35)');
    fadeGrad.addColorStop(1, 'rgba(10,8,24,0.95)');
    ctx.fillStyle = fadeGrad;
    ctx.fillRect(0, 0, W, PHOTO_H);
  }

  // Subtle radial glow top-right (offset below photo)
  const glowY = photoLoaded ? PHOTO_H * 0.5 : H * 0.08;
  const glow = ctx.createRadialGradient(W * 0.85, glowY, 0, W * 0.85, glowY, W * 0.55);
  glow.addColorStop(0, 'rgba(124,58,237,0.28)');
  glow.addColorStop(1, 'rgba(124,58,237,0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  // Subtle radial glow bottom-left
  const glow2 = ctx.createRadialGradient(W * 0.15, H * 0.88, 0, W * 0.15, H * 0.88, W * 0.5);
  glow2.addColorStop(0, 'rgba(6,182,212,0.18)');
  glow2.addColorStop(1, 'rgba(6,182,212,0)');
  ctx.fillStyle = glow2;
  ctx.fillRect(0, 0, W, H);

  // ── Decorative horizontal accent lines (below photo area) ───────────────
  const contentStart = photoLoaded ? PHOTO_H : 0;
  [0.5, 0.78].forEach((ratio) => {
    const ly = contentStart + (H - contentStart) * ratio;
    const lineGrad = ctx.createLinearGradient(0, 0, W, 0);
    lineGrad.addColorStop(0, 'rgba(124,58,237,0)');
    lineGrad.addColorStop(0.4, 'rgba(124,58,237,0.2)');
    lineGrad.addColorStop(0.6, 'rgba(6,182,212,0.2)');
    lineGrad.addColorStop(1, 'rgba(6,182,212,0)');
    ctx.save();
    ctx.strokeStyle = lineGrad;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, ly);
    ctx.lineTo(W, ly);
    ctx.stroke();
    ctx.restore();
  });

  // curY starts: just inside the photo (overlapping bottom third) or at top
  let curY = photoLoaded ? Math.round(PHOTO_H * 0.55) : 48;

  // ── Top accent bar (always at very top, over photo) ──────────────────────
  const accentGrad = ctx.createLinearGradient(0, 0, W, 0);
  accentGrad.addColorStop(0, '#7c3aed');
  accentGrad.addColorStop(1, '#06b6d4');
  ctx.fillStyle = accentGrad;
  ctx.fillRect(0, 0, W, 5);

  // ── Photo credit / source label (bottom-right of photo, if loaded) ────────
  if (photoLoaded) {
    ctx.save();
    ctx.fillStyle = 'rgba(255,255,255,0.45)';
    ctx.font = '14px Arial';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'bottom';
    ctx.fillText('AI Reference Photo', W - 12, PHOTO_H - 8);
    ctx.restore();
  }

  // ── Domain / trend pill ──────────────────────────────────────────────────
  if (trendName) {
    const label = trendName.toUpperCase().slice(0, 40);
    ctx.save();
    ctx.font = `bold 22px Arial`;
    const tw = ctx.measureText(`📌 ${label}`).width;
    const pillW = tw + 40;
    const pillH = 36;
    ctx.fillStyle = 'rgba(124,58,237,0.35)';
    ctx.strokeStyle = 'rgba(167,139,250,0.5)';
    ctx.lineWidth = 1.5;
    roundRect(ctx, PAD, curY, pillW, pillH, 18);
    ctx.fill();
    roundRect(ctx, PAD, curY, pillW, pillH, 18);
    ctx.stroke();
    ctx.fillStyle = '#c4b5fd';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(`📌 ${label}`, PAD + 20, curY + pillH / 2);
    ctx.restore();
    curY += pillH + 28;
  }

  // ── Title ─────────────────────────────────────────────────────────────────
  {
    const titleSize = 68;
    ctx.save();
    ctx.fillStyle = '#ffffff';
    ctx.font = `bold ${titleSize}px "Arial Black", Arial, sans-serif`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    const lines = wrapText(ctx, infographic.title, W - PAD * 2);
    // Cap at 2 lines to leave space
    lines.slice(0, 2).forEach((line, i) => {
      ctx.fillText(line, PAD, curY + i * (titleSize + 8));
    });
    curY += Math.min(lines.length, 2) * (titleSize + 8) + 12;
    ctx.restore();
  }

  // ── Subtitle ─────────────────────────────────────────────────────────────
  if (infographic.subtitle) {
    const subSize = 30;
    ctx.save();
    ctx.fillStyle = '#94a3b8';
    ctx.font = `${subSize}px Arial`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    const lines = wrapText(ctx, infographic.subtitle, W - PAD * 2);
    lines.slice(0, 2).forEach((line, i) => {
      ctx.fillText(line, PAD, curY + i * (subSize + 5));
    });
    curY += Math.min(lines.length, 2) * (subSize + 5) + 20;
    ctx.restore();
  }

  // ── Gradient divider ─────────────────────────────────────────────────────
  {
    const dg = ctx.createLinearGradient(PAD, 0, W - PAD, 0);
    dg.addColorStop(0, '#7c3aed');
    dg.addColorStop(1, '#06b6d4');
    ctx.save();
    ctx.strokeStyle = dg;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(PAD, curY);
    ctx.lineTo(W - PAD, curY);
    ctx.stroke();
    ctx.restore();
    curY += 32;
  }

  // ── Hero stat (first stat, full-width spotlight) ─────────────────────────
  const allStats = infographic.statHighlights?.slice(0, 3) || [];
  if (allStats.length > 0) {
    const hero = allStats[0];
    const heroCardH = 140;
    const heroCardW = W - PAD * 2;

    ctx.save();
    const hcg = ctx.createLinearGradient(PAD, curY, PAD + heroCardW, curY + heroCardH);
    hcg.addColorStop(0, 'rgba(124,58,237,0.55)');
    hcg.addColorStop(1, 'rgba(6,182,212,0.40)');
    ctx.fillStyle = hcg;
    roundRect(ctx, PAD, curY, heroCardW, heroCardH, 16);
    ctx.fill();
    ctx.strokeStyle = 'rgba(167,139,250,0.35)';
    ctx.lineWidth = 1.5;
    roundRect(ctx, PAD, curY, heroCardW, heroCardH, 16);
    ctx.stroke();

    // Giant value
    ctx.fillStyle = '#ffffff';
    ctx.font = `bold 82px "Arial Black", Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(hero.value, W / 2, curY + heroCardH * 0.44);

    // Label below
    ctx.fillStyle = '#c4b5fd';
    ctx.font = `bold 26px Arial`;
    ctx.fillText(hero.label.toUpperCase(), W / 2, curY + heroCardH * 0.80);
    ctx.restore();
    curY += heroCardH + 20;

    // ── 2 smaller stat cards ─────────────────────────────────────────────
    const rest = allStats.slice(1, 3);
    if (rest.length > 0) {
      const smCardW = (heroCardW - 16) / 2;
      const smCardH = 110;
      rest.forEach((s, i) => {
        const sx = PAD + i * (smCardW + 16);
        ctx.save();
        const sg = ctx.createLinearGradient(sx, curY, sx + smCardW, curY + smCardH);
        sg.addColorStop(0, 'rgba(15,23,42,0.80)');
        sg.addColorStop(1, 'rgba(30,41,59,0.80)');
        ctx.fillStyle = sg;
        roundRect(ctx, sx, curY, smCardW, smCardH, 14);
        ctx.fill();
        ctx.strokeStyle = i === 0 ? 'rgba(124,58,237,0.4)' : 'rgba(6,182,212,0.4)';
        ctx.lineWidth = 2;
        roundRect(ctx, sx, curY, smCardW, smCardH, 14);
        ctx.stroke();

        ctx.fillStyle = i === 0 ? '#a78bfa' : '#67e8f9';
        ctx.font = `bold 52px "Arial Black", Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(s.value, sx + smCardW / 2, curY + smCardH * 0.42);

        ctx.fillStyle = '#94a3b8';
        ctx.font = `bold 20px Arial`;
        ctx.fillText(s.label.toUpperCase().slice(0, 28), sx + smCardW / 2, curY + smCardH * 0.80);
        ctx.restore();
      });
      curY += smCardH + 28;
    }
  }

  // ── Section label: KEY INSIGHTS ──────────────────────────────────────────
  {
    ctx.save();
    ctx.fillStyle = '#475569';
    ctx.font = `bold 18px Arial`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.letterSpacing = '4px';
    ctx.fillText('KEY INSIGHTS', PAD, curY);
    ctx.restore();
    curY += 30;
  }

  // ── Data point bullets ────────────────────────────────────────────────────
  const points = infographic.dataPoints?.slice(0, 5) || [];
  const dotColors = ['#7c3aed', '#06b6d4', '#f59e0b', '#10b981', '#f472b6'];
  const ptFontSize = 28;
  const ptLineH = ptFontSize + 6;
  const dotRadius = 10;

  points.forEach((pt, i) => {
    const shortPt = pt.length > 100 ? pt.slice(0, 97) + '…' : pt;
    ctx.save();
    // Dot
    ctx.beginPath();
    ctx.arc(PAD + dotRadius, curY + dotRadius + 2, dotRadius, 0, Math.PI * 2);
    ctx.fillStyle = dotColors[i % dotColors.length];
    ctx.fill();
    // Number in dot
    ctx.fillStyle = '#fff';
    ctx.font = `bold 13px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(String(i + 1), PAD + dotRadius, curY + dotRadius + 2);

    // Text
    ctx.fillStyle = '#e2e8f0';
    ctx.font = `${ptFontSize}px Arial`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    const textX = PAD + dotRadius * 2 + 12;
    const lines = wrapText(ctx, shortPt, W - textX - PAD);
    // Cap at 2 lines per point
    lines.slice(0, 2).forEach((line, li) => {
      ctx.fillText(line, textX, curY + li * ptLineH);
    });
    curY += Math.min(lines.length, 2) * ptLineH + 14;
    ctx.restore();
  });

  curY += 10;

  // ── Bar chart (visual flair from stat values) ─────────────────────────────
  const chartStats = infographic.statHighlights?.slice(0, 3) || [];
  if (chartStats.length >= 2 && curY < H - 180) {
    const chartH = 90;
    const chartW = W - PAD * 2;
    const barPad = 12;
    const barW = (chartW - barPad * (chartStats.length - 1)) / chartStats.length;

    // Extract numeric value from string (e.g. "87%", "3.2x", "1,200")
    const numericValues = chartStats.map((s) => {
      const n = parseFloat(s.value.replace(/[^0-9.]/g, ''));
      return isNaN(n) ? 50 : Math.min(n, 100);
    });
    const maxVal = Math.max(...numericValues, 1);

    ctx.save();
    chartStats.forEach((s, i) => {
      const ratio = numericValues[i] / maxVal;
      const bx = PAD + i * (barW + barPad);
      const bh = Math.max(chartH * ratio, 12);
      const by = curY + chartH - bh;

      // Bar gradient
      const bg = ctx.createLinearGradient(bx, by, bx, by + bh);
      const barColors = [['#7c3aed', '#a78bfa'], ['#06b6d4', '#67e8f9'], ['#f59e0b', '#fcd34d']];
      const [c1, c2] = barColors[i % barColors.length];
      bg.addColorStop(0, c1);
      bg.addColorStop(1, c2);
      ctx.fillStyle = bg;
      roundRect(ctx, bx, by, barW, bh, 8);
      ctx.fill();

      // Value label on bar
      ctx.fillStyle = '#ffffff';
      ctx.font = `bold 20px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.fillText(s.value, bx + barW / 2, by - 4);

      // Label below chart
      ctx.fillStyle = '#64748b';
      ctx.font = `14px Arial`;
      ctx.textBaseline = 'top';
      ctx.fillText(s.label.slice(0, 18), bx + barW / 2, curY + chartH + 6);
    });
    ctx.restore();
    curY += chartH + 40;
  }

  // ── Footer bar ────────────────────────────────────────────────────────────
  const footerY = H - 52;
  const fg = ctx.createLinearGradient(0, footerY, W, footerY);
  fg.addColorStop(0, 'rgba(124,58,237,0.7)');
  fg.addColorStop(1, 'rgba(6,182,212,0.7)');
  ctx.fillStyle = fg;
  ctx.fillRect(0, footerY, W, 52);

  ctx.fillStyle = '#ffffff';
  ctx.font = `bold 20px Arial`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('Design Trend Content Factory  ·  2026', W / 2, footerY + 26);

  return canvas.toDataURL('image/png');
}

// ─── Comic Strip (6-panel 2×3 with unique FLUX art per panel) ────────────────

/**
 * Composites a 6-panel comic strip in a 2-column × 3-row portrait layout.
 *
 * panelImages: array of 6 base64 data URLs — one unique FLUX illustration
 *              per panel (may contain nulls for failed panels).
 * comic: the ComicStrip data with title, panels[], moralOfStory, style.
 *
 * Output: 1080 × 1440 portrait PNG (matches reference screenshot proportions).
 */
export async function compositeComic(
  panelImages: (string | null)[],
  comic: ComicStrip
): Promise<string> {
  // ── Canvas: 1080 × 1440 portrait (2×3 grid, matches reference screenshot) ──
  const W = 1080;
  const TITLE_H = 80;
  const FOOTER_H = 64;
  const GUTTER = 10;
  const COLS = 2;
  const ROWS = 3;
  const PANEL_COUNT = COLS * ROWS; // 6

  // Panel dimensions
  const panelW = (W - GUTTER * (COLS + 1)) / COLS;
  const panelH = 380; // fixed height per panel for clean layout
  const gridH = ROWS * panelH + GUTTER * (ROWS + 1);
  const H = TITLE_H + gridH + FOOTER_H;

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d')!;

  // ── Background ───────────────────────────────────────────────────────────
  const bgGrad = ctx.createLinearGradient(0, 0, W, H);
  bgGrad.addColorStop(0, '#0f0c29');
  bgGrad.addColorStop(1, '#1a1040');
  ctx.fillStyle = bgGrad;
  ctx.fillRect(0, 0, W, H);

  // Subtle grid background dots
  ctx.save();
  ctx.fillStyle = 'rgba(124,58,237,0.04)';
  for (let gx = 0; gx < W; gx += 36) {
    for (let gy = 0; gy < H; gy += 36) {
      ctx.beginPath();
      ctx.arc(gx, gy, 1.5, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.restore();

  // ── Load all panel images in parallel ────────────────────────────────────
  const imgs: (HTMLImageElement | null)[] = await Promise.all(
    (panelImages.slice(0, PANEL_COUNT) as (string | null)[]).map(async (src) => {
      if (!src) return null;
      try { return await loadImage(src); } catch { return null; }
    })
  );

  // ── Title banner ─────────────────────────────────────────────────────────
  const titleGrad = ctx.createLinearGradient(0, 0, W, TITLE_H);
  titleGrad.addColorStop(0, '#3b0764');
  titleGrad.addColorStop(0.6, '#4c1d95');
  titleGrad.addColorStop(1, '#0e7490');
  ctx.fillStyle = titleGrad;
  ctx.fillRect(0, 0, W, TITLE_H);

  // Top accent line
  const accentG = ctx.createLinearGradient(0, 0, W, 0);
  accentG.addColorStop(0, '#7c3aed');
  accentG.addColorStop(1, '#06b6d4');
  ctx.fillStyle = accentG;
  ctx.fillRect(0, 0, W, 4);

  // Title text — shrink font if title is very long
  ctx.save();
  const titleStr = comic.title.toUpperCase();
  let titleFontSize = 42;
  ctx.font = `bold ${titleFontSize}px "Arial Black", Arial`;
  while (ctx.measureText(titleStr).width > W - 40 && titleFontSize > 24) {
    titleFontSize -= 2;
    ctx.font = `bold ${titleFontSize}px "Arial Black", Arial`;
  }
  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(titleStr, W / 2, TITLE_H * 0.46);
  // Style subtitle
  ctx.fillStyle = 'rgba(196,181,253,0.85)';
  ctx.font = `italic 20px Arial`;
  ctx.fillText(`✦ ${comic.style} ✦`, W / 2, TITLE_H * 0.80);
  ctx.restore();

  // ── Draw 6 panels (2 cols × 3 rows) ──────────────────────────────────────
  const panels = comic.panels?.slice(0, PANEL_COUNT) || [];

  // Fixed text-strip height at bottom of each panel (art shows above this)
  const TEXT_STRIP_H = 100; // pixels reserved for caption + dialogue at panel bottom
  const ART_H = panelH - TEXT_STRIP_H; // art fills the top portion cleanly

  // Fallback gradient per panel when FLUX image is missing
  const fallbackGrads = [
    ['#1e1b4b', '#312e81'],
    ['#0c4a6e', '#164e63'],
    ['#2d1b69', '#0c3547'],
    ['#1a1a2e', '#0f3460'],
    ['#2d1b69', '#11998e'],
    ['#0c4a6e', '#1e1b4b'],
  ];

  for (let idx = 0; idx < Math.max(panels.length, 0); idx++) {
    const p = panels[idx];
    if (!p) continue;

    const col = idx % COLS;
    const row = Math.floor(idx / COLS);
    const px = GUTTER + col * (panelW + GUTTER);
    const py = TITLE_H + GUTTER + row * (panelH + GUTTER);

    // ── Zone 1: ART area (top portion, no text overlap) ─────────────────
    ctx.save();
    ctx.beginPath();
    // Clip to just the art zone (rounded top corners, square bottom for clean join)
    ctx.moveTo(px + 10, py);
    ctx.lineTo(px + panelW - 10, py);
    ctx.quadraticCurveTo(px + panelW, py, px + panelW, py + 10);
    ctx.lineTo(px + panelW, py + ART_H);
    ctx.lineTo(px, py + ART_H);
    ctx.lineTo(px, py + 10);
    ctx.quadraticCurveTo(px, py, px + 10, py);
    ctx.closePath();
    ctx.clip();

    const img = imgs[idx];
    if (img) {
      // Cover-fit art image into art zone
      const imgAspect = img.naturalWidth / img.naturalHeight;
      const artAspect = panelW / ART_H;
      let sx = 0, sy = 0, sw = img.naturalWidth, sh = img.naturalHeight;
      if (imgAspect > artAspect) {
        sw = img.naturalHeight * artAspect;
        sx = (img.naturalWidth - sw) / 2;
      } else {
        sh = img.naturalWidth / artAspect;
        sy = (img.naturalHeight - sh) / 2;
      }
      ctx.drawImage(img, sx, sy, sw, sh, px, py, panelW, ART_H);
      // Very light scrim at top only (for number badge readability)
      const scrim = ctx.createLinearGradient(0, py, 0, py + 50);
      scrim.addColorStop(0, 'rgba(0,0,0,0.45)');
      scrim.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = scrim;
      ctx.fillRect(px, py, panelW, 50);
    } else {
      const [c1, c2] = fallbackGrads[idx % fallbackGrads.length];
      const fg = ctx.createLinearGradient(px, py, px + panelW, py + ART_H);
      fg.addColorStop(0, c1);
      fg.addColorStop(1, c2);
      ctx.fillStyle = fg;
      ctx.fillRect(px, py, panelW, ART_H);
      // "Generating…" hint in fallback
      ctx.fillStyle = 'rgba(167,139,250,0.35)';
      ctx.font = 'bold 28px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(p.emoji || '🎨', px + panelW / 2, py + ART_H / 2);
    }
    ctx.restore();

    // ── Zone 2: TEXT STRIP (bottom strip, solid dark background) ────────
    const textY = py + ART_H;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(px, textY);
    ctx.lineTo(px + panelW, textY);
    ctx.lineTo(px + panelW, py + panelH - 10);
    ctx.quadraticCurveTo(px + panelW, py + panelH, px + panelW - 10, py + panelH);
    ctx.lineTo(px + 10, py + panelH);
    ctx.quadraticCurveTo(px, py + panelH, px, py + panelH - 10);
    ctx.lineTo(px, textY);
    ctx.closePath();
    ctx.fillStyle = 'rgba(10,6,30,0.97)';
    ctx.fill();
    ctx.restore();

    // Caption text (italic, top of strip)
    const captionText = (p.caption || '').trim();
    const dialogueText = (p.dialogue || '').trim();
    const capFontSize = 14;
    const dlgFontSize = 13;
    const stripPad = 7;

    if (captionText) {
      ctx.save();
      ctx.font = `italic ${capFontSize}px Arial`;
      const capLines = wrapText(ctx, captionText, panelW - stripPad * 2).slice(0, 2);
      ctx.fillStyle = 'rgba(196,181,253,0.9)'; // soft violet
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      capLines.forEach((line, li) => {
        ctx.fillText(line, px + stripPad, textY + stripPad + li * (capFontSize + 2));
      });
      ctx.restore();
    }

    // Dialogue text (bold, below caption, white)
    if (dialogueText) {
      ctx.save();
      const capLinesCount = captionText ? Math.min(wrapText(ctx, captionText, panelW - stripPad * 2).length, 2) : 0;
      const dlgStartY = textY + stripPad + capLinesCount * (capFontSize + 2) + (capLinesCount > 0 ? 4 : 0);
      ctx.font = `bold ${dlgFontSize}px Arial`;
      const dlgLines = wrapText(ctx, `"${dialogueText}"`, panelW - stripPad * 2).slice(0, 3);
      ctx.fillStyle = '#f8fafc';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      dlgLines.forEach((line, li) => {
        ctx.fillText(line, px + stripPad, dlgStartY + li * (dlgFontSize + 2));
      });
      ctx.restore();
    }

    // ── Number badge (top-left of art zone, always on top) ──────────────
    ctx.save();
    const badgeX = px + 16;
    const badgeY = py + 16;
    const badgeR = 13;
    ctx.beginPath();
    ctx.arc(badgeX, badgeY, badgeR, 0, Math.PI * 2);
    ctx.fillStyle = '#7c3aed';
    ctx.shadowColor = 'rgba(0,0,0,0.8)';
    ctx.shadowBlur = 6;
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.fillStyle = '#ffffff';
    ctx.font = `bold 13px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(String(p.panel || idx + 1), badgeX, badgeY);
    ctx.restore();

    // ── Emoji badge (top-right of art zone) ─────────────────────────────
    if (p.emoji) {
      ctx.save();
      ctx.font = `20px Arial`;
      ctx.textAlign = 'right';
      ctx.textBaseline = 'top';
      ctx.fillText(p.emoji, px + panelW - 8, py + 6);
      ctx.restore();
    }

    // ── Outer panel border ───────────────────────────────────────────────
    ctx.save();
    ctx.strokeStyle = 'rgba(139,92,246,0.6)';
    ctx.lineWidth = 2;
    roundRect(ctx, px, py, panelW, panelH, 10);
    ctx.stroke();
    ctx.restore();
  }

  // ── Moral-of-story footer ─────────────────────────────────────────────────
  const moralY = H - FOOTER_H;
  const moralGrad = ctx.createLinearGradient(0, moralY, W, moralY + FOOTER_H);
  moralGrad.addColorStop(0, 'rgba(124,58,237,0.92)');
  moralGrad.addColorStop(1, 'rgba(6,182,212,0.92)');
  ctx.fillStyle = moralGrad;
  ctx.fillRect(0, moralY, W, FOOTER_H);

  ctx.save();
  ctx.fillStyle = '#ffffff';
  const moralText = `✨ ${(comic.moralOfStory || '').slice(0, 95)}`;
  let moralFontSize = 22;
  ctx.font = `bold ${moralFontSize}px Arial`;
  while (ctx.measureText(moralText).width > W - 40 && moralFontSize > 14) {
    moralFontSize -= 1;
    ctx.font = `bold ${moralFontSize}px Arial`;
  }
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(moralText, W / 2, moralY + FOOTER_H / 2);
  ctx.restore();

  return canvas.toDataURL('image/png');
}
